import { type ChangeEvent, type Ref, type TextareaHTMLAttributes, useState } from 'react';

import { cn } from '@shared/lib/cn';

import {
  DEFAULT_STATE_CLASS,
  DISABLED_STATE_CLASS,
  INVALID_STATE_CLASS,
  type TextareaStatus,
  type TextareaTone,
  TONE_CLASS,
} from './textarea.constant';
import { TextareaSkeleton } from './textarea.skeleton.ui';

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'defaultValue'
> {
  /** Figma `tone`. plain = 테두리 없음(메모 본문), soft = 옅은 primary 면(그래프 코멘트), green = 옅은 연두 면. 기본 default */
  tone?: TextareaTone;
  /** 제어 값. `filled` 는 값이 비었는지로 정해진다 */
  value?: string;
  /** 비제어 초기값 */
  defaultValue?: string;
  /** Figma `state=error` */
  invalid?: boolean;
  /** Figma `status`. init 이면 스켈레톤 */
  status?: TextareaStatus;
  /** 너비 · 높이는 여기로. 기본은 부모 너비 × 96 */
  className?: string;
  ref?: Ref<HTMLTextAreaElement>;
}

/** 여러 줄 입력 */
export function Textarea({
  tone = 'default',
  value,
  defaultValue,
  onChange,
  invalid = false,
  status = 'default',
  className,
  disabled,
  ref,
  ...rest
}: TextareaProps) {
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(() => defaultValue ?? '');
  const filled = (isControlled ? value : innerValue).length > 0;

  if (status === 'init') return <TextareaSkeleton className={className} />;

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) setInnerValue(event.target.value);
    onChange?.(event);
  };

  const stateClass = disabled
    ? DISABLED_STATE_CLASS[tone]
    : invalid
      ? INVALID_STATE_CLASS[tone]
      : DEFAULT_STATE_CLASS[tone];

  return (
    <textarea
      ref={ref}
      {...rest}
      {...(isControlled ? { value } : { defaultValue })}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      onChange={handleChange}
      data-tone={tone}
      data-filled={filled}
      className={cn(
        'block h-24 w-full resize-none text-fg-default outline-none placeholder:text-fg-placeholder disabled:cursor-not-allowed disabled:text-fg-disabled disabled:placeholder:text-fg-disabled',
        TONE_CLASS[tone],
        stateClass,
        className,
      )}
    />
  );
}
